import {
  useMemo,
  useState,
} from "react";

import {
  supabase,
} from "../../lib/supabaseClient";

import {
  prpWorkspaceCommand,
} from "../../lib/prpWorkspace";

import {
  usePrpSessionState,
} from "../../lib/prpUiContinuity";

// LOT0F8_PRP_PARTNER_CONVERSION_COCKPIT_V1

type AnyRecord = Record<string, any>;

type Props = {
  leads: AnyRecord[];
  activities: AnyRecord[];
  onRefresh: () => Promise<void> | void;
};

type Filter = "all" | "ready" | "candidate";

type ForfyState = {
  lead_id: string;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  payload?: AnyRecord;
  error?: string;
};

const text = (value: unknown, fallback = ""): string =>
  typeof value === "string" && value.trim()
    ? value.trim()
    : fallback;

const stageOf = (lead: AnyRecord): string =>
  text(lead.pipeline_stage || lead.status, "new").toLowerCase();

const hasNamedContact = (lead: AnyRecord): boolean => {
  const value = text(lead.contact_name);
  return Boolean(value && !/^contact à identifier$/i.test(value));
};

const hasChannel = (lead: AnyRecord): boolean =>
  Boolean(text(lead.email) || text(lead.phone));

const readiness = (lead: AnyRecord) => {
  const stage = stageOf(lead);
  const checks = [
    { key: "business", label: "Entreprise identifiée", passed: Boolean(text(lead.business_name)), points: 20 },
    { key: "contact", label: "Décideur ou contact identifié", passed: hasNamedContact(lead), points: 20 },
    { key: "channel", label: "Courriel ou téléphone disponible", passed: hasChannel(lead), points: 20 },
    { key: "progress", label: "Prospect déjà travaillé", passed: !["new", "lost"].includes(stage), points: 15 },
    { key: "next", label: "Prochaine action définie", passed: Boolean(text(lead.next_action)), points: 15 },
    { key: "notes", label: "Contexte CRM disponible", passed: text(lead.notes).length >= 20, points: 10 },
  ];

  const score = checks.reduce(
    (total, item) => total + (item.passed ? item.points : 0),
    0,
  );

  const minimumIdentityReady =
    Boolean(text(lead.business_name))
    && hasNamedContact(lead)
    && hasChannel(lead);

  return {
    checks,
    score,
    ready: minimumIdentityReady && score >= 70,
  };
};

const invokeForfyOnboarder = async (
  lead: AnyRecord,
  readinessScore: number,
): Promise<AnyRecord> => {
  const { data: { session }, error: sessionError } =
    await supabase.auth.getSession();

  if (sessionError) throw sessionError;

  const token = session?.access_token?.trim();
  if (!token) throw new Error("prp_session_required");

  const result = await supabase.functions.invoke(
    "prp-forfy-agent-run",
    {
      body: {
        action: "run_prp_forfy_agent",
        agent_key: "forfy_prp_onboarder",
        context: {
          business_name: text(lead.business_name, "Prospect"),
          contact_name: text(lead.contact_name),
          email: text(lead.email),
          phone: text(lead.phone),
          status: stageOf(lead),
          notes: text(lead.notes),
          next_action: text(lead.next_action),
          readiness_score: readinessScore,
          source: "prp_partner_conversion",
          instruction: "Prépare un dossier d’onboarding commercial concis: informations manquantes, proposition de valeur Forfeo pertinente, étapes d’adhésion et points à valider. N’invente aucune donnée, ne crée aucun compte, n’approuve aucun partenaire et n’exécute aucune transaction.",
        },
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (result.error) {
    const errorData = result.data as AnyRecord | null;
    throw new Error(
      typeof errorData?.error === "string"
        ? String(errorData.error)
        : result.error.message || "forfy_onboarder_failed",
    );
  }

  const payload = (result.data || {}) as AnyRecord;
  if (payload.ok !== true) {
    throw new Error(text(payload.error, "forfy_onboarder_failed"));
  }

  return payload;
};

export default function PrpPartnerConversionCockpit({
  leads,
  activities,
  onRefresh,
}: Props) {
  const [selectedLeadId, setSelectedLeadId] = usePrpSessionState<string | null>(
    "partner-conversion:selected-lead",
    null,
    (value) => value === null || typeof value === "string",
  );

  const [filter, setFilter] = usePrpSessionState<Filter>(
    "partner-conversion:filter",
    "all",
    (value) => ["all", "ready", "candidate"].includes(String(value)),
  );

  const [busyLeadId, setBusyLeadId] = useState("");
  const [forfy, setForfy] = useState<ForfyState | null>(null);

  const commercialLeads = useMemo(
    () => leads.filter((lead) => !["lost", "active"].includes(stageOf(lead))),
    [leads],
  );

  const readyCount = useMemo(
    () => commercialLeads.filter((lead) => readiness(lead).ready).length,
    [commercialLeads],
  );

  const candidateCount = useMemo(
    () => leads.filter((lead) => stageOf(lead) === "registered").length,
    [leads],
  );

  const approvedCount = useMemo(
    () => leads.filter((lead) => ["approved", "active"].includes(stageOf(lead))).length,
    [leads],
  );

  const visibleLeads = useMemo(
    () => commercialLeads
      .filter((lead) => {
        if (filter === "ready") return readiness(lead).ready;
        if (filter === "candidate") return stageOf(lead) === "registered";
        return true;
      })
      .sort((a, b) => readiness(b).score - readiness(a).score),
    [commercialLeads, filter],
  );

  const selectedLead = useMemo(
    () => leads.find((lead) => text(lead.id) === selectedLeadId) || null,
    [leads, selectedLeadId],
  );

  const selectedReadiness = selectedLead ? readiness(selectedLead) : null;

  const runOnboarder = async () => {
    if (!selectedLead || !selectedReadiness) return;
    const leadId = text(selectedLead.id);
    setForfy({ lead_id: leadId, loading: true, saving: false, saved: false });
    try {
      const payload = await invokeForfyOnboarder(selectedLead, selectedReadiness.score);
      setForfy({ lead_id: leadId, loading: false, saving: false, saved: false, payload });
    } catch (error) {
      setForfy({
        lead_id: leadId,
        loading: false,
        saving: false,
        saved: false,
        error: error instanceof Error ? error.message : "forfy_onboarder_failed",
      });
    }
  };

  const saveOnboarder = async () => {
    if (!selectedLead || !forfy?.payload || forfy.saving || forfy.saved) return;
    const leadId = text(selectedLead.id);
    const resultId = text(forfy.payload.forfy_result_id);
    if (!leadId || !resultId) return;

    setForfy((current) => current ? { ...current, saving: true } : current);
    try {
      await prpWorkspaceCommand("forfy_result_save", {
        forfy_result_id: resultId,
        agent_key: "forfy_prp_onboarder",
        lead_id: leadId,
        business_name: text(selectedLead.business_name, "Prospect"),
        output: forfy.payload.output || {},
        runtime_meta: forfy.payload.runtime_meta || {},
        canonical_product_context_version:
          forfy.payload.canonical_product_context_version || "forfeo-products-v1",
      });
      setForfy((current) => current ? { ...current, saving: false, saved: true } : current);
      await Promise.resolve(onRefresh());
    } catch (error) {
      setForfy((current) => current ? {
        ...current,
        saving: false,
        error: error instanceof Error ? error.message : "forfy_memory_save_failed",
      } : current);
    }
  };

  const submitCandidate = async () => {
    if (!selectedLead || !selectedReadiness?.ready) return;
    const leadId = text(selectedLead.id);
    const stage = stageOf(selectedLead);
    if (!leadId || ["registered", "approved", "active"].includes(stage)) return;

    setBusyLeadId(leadId);
    try {
      await prpWorkspaceCommand("partner_candidate_submit", {
        lead_id: leadId,
        readiness_score: selectedReadiness.score,
        human_confirmation: true,
      });
      await Promise.resolve(onRefresh());
      window.dispatchEvent(new CustomEvent("forfeo:prp-workspace-refresh", {
        detail: { source: "partner_candidate_submit", lead_id: leadId },
      }));
    } finally {
      setBusyLeadId("");
    }
  };

  return (
    <section
      className="rounded-3xl border border-sky-100 bg-gradient-to-br from-white via-white to-sky-50 p-5 shadow-sm"
      data-lot0f8-partner-conversion="true"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.24em] text-sky-700">
            0F-8 · Conversion partenaire
          </p>
          <h3 className="mt-1 text-xl font-black text-slate-950">
            Prospect → candidat partenaire
          </h3>
          <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
            Prépare le dossier commercial, utilise Forfy Onboarder, puis soumets explicitement le prospect comme candidat. L’approbation et l’activation restent exclusivement sous contrôle administrateur.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2">
            <p className="text-lg font-black text-emerald-900">{readyCount}</p>
            <p className="text-[10px] font-black uppercase text-emerald-700">prêts</p>
          </div>
          <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-2">
            <p className="text-lg font-black text-sky-900">{candidateCount}</p>
            <p className="text-[10px] font-black uppercase text-sky-700">candidats</p>
          </div>
          <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2">
            <p className="text-lg font-black text-violet-900">{approvedCount}</p>
            <p className="text-[10px] font-black uppercase text-violet-700">admin+</p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {([[
          "all", "Tous",
        ], [
          "ready", "Prêts",
        ], [
          "candidate", "En attente validation admin",
        ]] as Array<[Filter, string]>).map(([key, label]) => (
          <button
            className={`rounded-full border px-4 py-2 text-xs font-black ${filter === key ? "border-sky-500 bg-sky-100 text-sky-950" : "border-slate-200 bg-white text-slate-600"}`}
            key={key}
            onClick={() => setFilter(key)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_430px]">
        <div className="grid content-start gap-3 md:grid-cols-2">
          {visibleLeads.map((lead) => {
            const id = text(lead.id);
            const state = readiness(lead);
            return (
              <button
                className={`rounded-2xl border p-4 text-left ${selectedLeadId === id ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-white"}`}
                key={id}
                onClick={() => {
                  setSelectedLeadId(id);
                  setForfy(null);
                }}
                type="button"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-black text-slate-950">{text(lead.business_name, "Prospect")}</p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">{text(lead.contact_name, "Contact à identifier")}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${state.ready ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {state.score}%
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase text-slate-500">{stageOf(lead)}</span>
                  {stageOf(lead) === "registered" && (
                    <span className="rounded-full bg-sky-100 px-3 py-1 text-[10px] font-black uppercase text-sky-800">validation admin</span>
                  )}
                </div>
              </button>
            );
          })}
          {visibleLeads.length === 0 && (
            <div className="md:col-span-2 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-400">
              Aucun prospect pour ce filtre.
            </div>
          )}
        </div>

        <aside className="rounded-2xl border border-slate-200 bg-white p-4">
          {!selectedLead || !selectedReadiness ? (
            <div className="flex min-h-80 items-center justify-center text-center">
              <div>
                <p className="text-lg font-black text-slate-900">Dossier partenaire</p>
                <p className="mt-2 text-sm font-semibold text-slate-500">Sélectionne un prospect pour voir sa préparation à l’adhésion.</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-sky-700">Dossier sélectionné</p>
                  <h4 className="mt-1 text-xl font-black text-slate-950">{text(selectedLead.business_name, "Prospect")}</h4>
                </div>
                <div className={`rounded-2xl px-3 py-2 text-center ${selectedReadiness.ready ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
                  <p className="text-xl font-black">{selectedReadiness.score}%</p>
                  <p className="text-[9px] font-black uppercase">préparation</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {selectedReadiness.checks.map((item) => (
                  <div className={`flex items-center justify-between rounded-xl border px-3 py-2 ${item.passed ? "border-emerald-100 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} key={item.key}>
                    <p className="text-xs font-bold text-slate-700">{item.label}</p>
                    <span className={`text-xs font-black ${item.passed ? "text-emerald-700" : "text-slate-400"}`}>{item.passed ? "✓" : `+${item.points}`}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-violet-100 bg-violet-50 p-4">
                <p className="text-sm font-black text-violet-950">Forfy Onboarder</p>
                <p className="mt-1 text-xs font-semibold leading-5 text-violet-700">
                  Prépare les informations manquantes et les étapes d’adhésion sans créer ni approuver le partenaire.
                </p>
                <button className="mt-3 w-full rounded-xl bg-violet-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40" disabled={forfy?.loading === true} onClick={runOnboarder} type="button">
                  {forfy?.loading ? "Forfy prépare le dossier…" : "Forfy · préparer l’onboarding"}
                </button>
                {forfy?.error && <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-2 text-xs font-bold text-rose-800">{forfy.error}</div>}
                {forfy?.payload && (
                  <div className="mt-3">
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-3 text-[11px] font-semibold text-slate-100">{JSON.stringify(forfy.payload.output || {}, null, 2)}</pre>
                    <button className="mt-2 w-full rounded-xl border border-violet-200 bg-white px-3 py-2 text-xs font-black text-violet-900 disabled:opacity-50" disabled={forfy.saving || forfy.saved} onClick={saveOnboarder} type="button">
                      {forfy.saving ? "Conservation…" : forfy.saved ? "✓ Dossier Forfy conservé" : "Conserver dans l’historique CRM"}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5 rounded-2xl border border-sky-200 bg-sky-50 p-4">
                {["approved", "active"].includes(stageOf(selectedLead)) ? (
                  <>
                    <p className="font-black text-sky-950">Contrôle administrateur actif</p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-sky-800">Ce dossier a atteint une étape réservée à l’administration. Le PRP ne peut ni approuver ni activer ce partenaire.</p>
                  </>
                ) : stageOf(selectedLead) === "registered" ? (
                  <>
                    <p className="font-black text-sky-950">✓ Candidat soumis</p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-sky-800">En attente de validation administrateur. Aucun compte commerce n’a été créé et aucune publication n’est autorisée.</p>
                  </>
                ) : (
                  <>
                    <p className="font-black text-sky-950">Soumettre comme candidat partenaire</p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-sky-800">Le dossier doit avoir au moins 70% et contenir un contact identifié ainsi qu’un moyen de joindre ce contact.</p>
                    <button className="mt-3 w-full rounded-xl bg-sky-700 px-4 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-40" disabled={!selectedReadiness.ready || busyLeadId === text(selectedLead.id)} onClick={submitCandidate} type="button">
                      {busyLeadId === text(selectedLead.id) ? "Soumission…" : selectedReadiness.ready ? "Soumettre à la validation admin" : "Dossier incomplet"}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-900">
        Autorité : le PRP peut préparer et soumettre un candidat, mais ne peut pas passer un prospect à Approuvé ou Actif. Aucun compte commerce, aucune offre, aucun paiement et aucune publication ne sont créés automatiquement.
      </div>
    </section>
  );
}
